import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild, HostListener, OnDestroy } from '@angular/core';
import { PatientPortalService } from '../../patient-portal/patient-portal.service';
import { UserService } from 'src/app/services/user.service';
import { StaticDataService } from 'src/app/services/static-data.service';
import { TreatmentPlanningDataModel } from '../models/treatment-planning-data.model';
import { TreatmentPlanningProcedurePseudoModel } from '../models/treatment-planning-procedure-pseudo.model';
import { BlobModel } from 'src/app/models/blob/blob.model';
import { Globals } from 'src/app/globals';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AzureBlobStorageService } from 'src/app/services/blob/azure-blob-storage.service';
import { DialogService } from 'src/app/services/dialog.service';
import { MessagingConstants } from 'src/app/models/MessagingConstants';
import { MessagingService } from 'src/app/services/messaging.service';
import { FileManagerService } from '../../file-manager/file-manager.service';
import SignaturePad from 'signature_pad';
import { UiService } from 'src/app/services/ui.service';

@Component({
  standalone: false,
  selector: 'app-treatment-plan-approval',
  templateUrl: './treatment-plan-approval.component.html',
  styleUrls: ['./treatment-plan-approval.component.scss']
})
export class TreatmentPlanApprovalComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() treatmentPlanId: string;
  //@ViewChild(SignaturePad) signaturePad: SignaturePad;
  //@ViewChild('padContainer') padContainer: ElementRef;
  @ViewChild('signatureContainer') signatureContainer: ElementRef<HTMLCanvasElement>;
  signaturePad: SignaturePad;
  private _padInitialized = false;

  isLoading: boolean = false;
  isSigned: boolean = false;
  patientId: string;
  treatment: TreatmentPlanningDataModel;
  signatureBase64: SafeUrl | string;
  signaturePadOptions: object = {
    minWidth: 2,
    canvasHeight: 130,
    canvasWidth: 700
  };

  isApprovalRequired: boolean = false;
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.updateSignaturePadSize();
  }

  constructor(
    public globals: Globals,
    private readonly staticDataService: StaticDataService,
    private readonly patientPortalService: PatientPortalService,
    private readonly dialogService: DialogService,
    private readonly sanitizer: DomSanitizer,
    private readonly azureService: AzureBlobStorageService,
    private readonly messagingService: MessagingService,
    private readonly fileManagerService: FileManagerService,
    private readonly uiService: UiService,
    private readonly elementRef: ElementRef,
    private readonly cdr: ChangeDetectorRef
  ) {

    this.patientId = this.globals.currentPatientId;

  }

  ngOnInit() {
    console.log('TreatmentPlanApprovalComponent ngOnInit, treatmentPlanId: ', this.treatmentPlanId);
    if (UserService.authToken && !this.staticDataService.isReady) {
      console.log('TreatmentPlanApprovalComponent ngOnInit refreshValues');
      this.staticDataService.refreshValues(true, false,true,true);
    }

    this.updateSignaturePadSize();

  const promises = [
      this.getTreatmentPlan()
    ];

    this.isLoading = true;

    this.cdr.markForCheck();
    Promise.all(promises).then((results: any[]) => {
      this.treatment = results[0];
      this.isApprovalRequired = this.treatment.enumTreatmentPlanStatusID.toLowerCase() === '00000a32-1000-0000-0000-000000000002';
      console.log('TreatmentPlanApprovalComponent ngOnInit: isApprovalRequired', this.isApprovalRequired);
      this.isLoading = false;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.getSignature();
      });
    }, err => {
      this.displayMessage("Error", "Something went wrong");
      this.isLoading = false;
    }).catch(error => {
      this.displayMessage("Error", "Something went wrong");
      console.error("Caught the error:", error); // Logs "Caught the error: Something went wrong!"
    });
  }

  ngAfterViewChecked() {
    if (this.signatureContainer && this.treatment && !this._padInitialized) {
      this._padInitialized = true;
      this.initSignaturePad();
    }
  }

  initSignaturePad() {
    console.log('Initializing signature pad');
    if (!this.signatureContainer){
      console.error('Signature container not found');
      return;
    }
    this.signaturePad = new SignaturePad(this.signatureContainer.nativeElement);
    this.signaturePad.addEventListener("beginStroke", () => {
      this.onSignatureDrawStart();
    });
    this.signaturePad.addEventListener("endStroke", () => {
      this.onSignatureDrawComplete();
    });
  }
  ngOnDestroy() {
    // Cleanup if needed
  }

  updateSignaturePadSize() {
    console.log('Updating signature pad size');
    const isMobile = window.innerWidth <= 768;
    const containerWidth = isMobile ? window.innerWidth - 32 : 700; // Account for padding
    
    this.signaturePadOptions = {
      minWidth: 2,
      canvasHeight: isMobile ? 100 : 130,
      canvasWidth: containerWidth
    };

    // Resize existing signature pad if it exists
    if (this.signaturePad) {
      setTimeout(() => {
        //this.signatureContainer.resizeCanvas(containerWidth, this.signaturePadOptions['canvasHeight']);
        this.signatureContainer.nativeElement.width = this.signaturePadOptions['canvasWidth'];
      this.signatureContainer.nativeElement.height = this.signaturePadOptions['canvasHeight'];
      }, 100);
    }
  }

  getTreatmentPlan() {
    console.log('Fetching treatment plan with ID:', this.treatmentPlanId);
    let promise = new Promise((resolve, reject) => {
      this.patientPortalService.getPatientTreatmentPlan(this.patientId, this.treatmentPlanId)
        .subscribe({ next: resolve, error: reject });
    });
    return promise;
  }
  displayMessage(type, message) {
    this.uiService.displayMessage(type, message);
  }


  getFee(procedure: TreatmentPlanningProcedurePseudoModel): string {
    if (procedure.PriceAdjusted !== null && procedure.PriceAdjusted !== 0) {
      return procedure.PriceAdjusted.toFixed(2);
    } else {
      return '0.00';
    }
  }

  getTotalFee(){
    return this.treatment.Procedures.reduce((n, {PriceAdjusted}) => n + PriceAdjusted, 0).toFixed(2);
  }

  getInsuranceEstimate(procedure: TreatmentPlanningProcedurePseudoModel): string {
    if (procedure.PriceTotalDueByInsurance != null && procedure.PriceTotalDueByInsurance !== 0) {
      return procedure.PriceTotalDueByInsurance.toFixed(2);
    } else {
      return '0.00';
    }
  }

  getTotalInsuranceEstimate(){
    return this.treatment.Procedures.reduce((n, {PriceTotalDueByInsurance}) => n + PriceTotalDueByInsurance, 0).toFixed(2);
  }

  getDeductible(procedure: TreatmentPlanningProcedurePseudoModel): string {
    // Currently deductible isn't being brought across
    if (procedure.Deductible != null) {
      return procedure.Deductible.toFixed(2);
    } else {
      return '0.00';
    }
  }

  getTotalDeductible(){
    return this.treatment.Procedures.reduce((n, {Deductible}) => n + Deductible, 0).toFixed(2);
  }

  getPaymentEstimate(procedure: TreatmentPlanningProcedurePseudoModel): string {
    if (procedure.PriceDueByPatient !== null && procedure.PriceDueByPatient !== 0) {
      return procedure.PriceDueByPatient.toFixed(2);
    } else {
      return '0.00';
    }
  }

  getTotalPaymentEstimate(){
    return this.treatment.Procedures.reduce((n, {PriceDueByPatient}) => n + PriceDueByPatient, 0).toFixed(2);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(text || ''));
    return div.innerHTML;
  }

  ApproveTreatment() {
    this.dialogService.openConfirmDialog(`Do you want to approve Treatment Option <br/><b>${this.escapeHtml(this.treatment.Description)}</b>?`).subscribe((confirmSend) => {
      if (confirmSend) {
        this.signatureBase64 = this.signaturePad.toDataURL();
        this.isLoading = true;
        this.patientPortalService.sendPatientTreatmentPlanApproval(this.patientId, this.treatment.TreatmentPlanningID).subscribe(resp => {
          if (resp) {
            this.treatment.enumTreatmentPlanStatusID = '00000a32-1000-0000-0000-000000000004';
            this.messagingService.sendComponentMessage(MessagingConstants.PATIENT_TREATMENT_APPROVED, { treatmentPlanId: this.treatment.TreatmentPlanningID });
            this.isLoading = false;
          }
        },
          error => {
            this.displayMessage("Error", "Error sending approval for treatment.");
            this.isLoading = false;
            return false;
          },
          () => {
            this.isLoading = false;
            return true;
          }
        );
      }
    });
  }

  RejectTreatment() {
    this.dialogService.openConfirmDialog(`Do you want to reject Treatment Option <br/><b>${this.escapeHtml(this.treatment.Description)}</b>?`).subscribe((confirmSend) => {
      if (confirmSend) {
        console.log('TreatmentPlanningComponent sendPatientTreatmentPlanRejection call.');
        this.isLoading = true;
        this.patientPortalService.sendPatientTreatmentPlanRejection(this.patientId, this.treatment.TreatmentPlanningID).subscribe(resp => {
          console.log('TreatmentPlanningComponent sendPatientTreatmentPlanRejection response: ', resp);
          if (resp) {
            this.isLoading = false;
            console.log('TreatmentPlanningComponent sendPatientTreatmentPlanRejection success, sendComponentMessage.');
            this.treatment.enumTreatmentPlanStatusID = '00000a32-1000-0000-0000-000000000003';
            this.messagingService.sendComponentMessage(MessagingConstants.PATIENT_TREATMENT_REJECTED, { treatmentPlanId: this.treatment.TreatmentPlanningID });
          }
        },
          error => {
            this.isLoading = false;
            this.displayMessage("Error", "Error sending rejection for treatment.");
          }
        );
      }
    });
  }

  getSignature() {
    console.log('TreatmentPlanApprovalComponent getSignature');
    if (!this.signaturePad){
      console.error('Signature pad not initialized');
      return;
    }
    console.log('TreatmentPlanApprovalComponent getSignature: ', this.treatment?.TreatmentPlanningID);
    if (this.treatment?.TreatmentPlanningID) {
      const signatureFilename = this.fileManagerService.getDocumentImageUrl(this.globals.currentPatientId, this.treatment.TreatmentPlanningID + '.jpg?t=' + Date.now());
      this.azureService.downloadBlob(signatureFilename, (res) => {
        setTimeout(() => {
          console.log('TreatmentPlanApprovalComponent getSignature: setTimeout');
          if (res) {
            console.log('TreatmentPlanApprovalComponent getSignature: res');
            const objectURL = URL.createObjectURL(res);
            
            if (this.treatment?.enumTreatmentPlanStatusID.toLowerCase()==='00000a32-1000-0000-0000-000000000002'){
              console.log('TreatmentPlanApprovalComponent getSignature: fromDataURL');
              //this.resizeSignature();
              this.signaturePad.fromDataURL(objectURL);
            }
            else{
              console.log('TreatmentPlanApprovalComponent getSignature: sanitizer.bypassSecurityTrustUrl');
              this.signatureBase64 = this.sanitizer.bypassSecurityTrustUrl(objectURL);
              console.log('TreatmentPlanApprovalComponent getSignature: signatureBase64', this.signatureBase64);
            }
            this.isSigned = true;
            console.log('TreatmentPlanApprovalComponent getSignature: isSigned', this.isSigned);
          } else {
            this.signatureBase64 = null;
          }          
          this.cdr.markForCheck();
        });
      });
    }
  }

  onSignatureDrawComplete() {
    // will be notified of szimek/signature_pad's onEnd event
    const drawData = this.signaturePad.toDataURL();
    fetch(drawData)
      .then(res => res.blob())
      .then((tmp) => {
        const blobModel = new BlobModel();
        blobModel.name = this.treatment.TreatmentPlanningID + '.jpg';
        blobModel.content = tmp;
        blobModel.patientId = this.patientId;
        this.azureService.uploadBlobs([blobModel], (isSuccess) => {
          this.isSigned = true;
          this.cdr.markForCheck();
        });
      });
  }

  onSignatureDrawStart() { }


  resetSignature() {
    this.signaturePad.clear();
    this.isSigned = false;
  }
}
